import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, MessageSquare, FolderClosed, Globe, Loader2, Check, Sparkles, TriangleAlert, Play, Calculator, KeyRound } from 'lucide-react';
import { api, type ImportJob, type ImportSourceBody } from '../lib/api';

// Compte de service M365 sous lequel l'indexation Teams/SharePoint s'exécute.
// Il ne lit QUE les équipes/sites auxquels ce compte a été explicitement ajouté.
const SERVICE_ACCOUNT = 'gnosis-import@studentfr.ch';

type Tab = 'teams' | 'sharepoint' | 'web';
const TABS: { key: Tab; icon: typeof Globe }[] = [
  { key: 'teams', icon: MessageSquare },
  { key: 'sharepoint', icon: FolderClosed },
  { key: 'web', icon: Globe },
];

/**
 * Console d'import (étage 2, Importateurs) : enveloppe les formules d'ingestion
 * (Teams / SharePoint / web) dans une UI guidée. « Estimer » est gratuit (liste +
 * coût IA) ; « Importer » lance un job en arrière-plan suivi par polling.
 * L'enrichissement IA est **systématique** (imposé côté serveur) : un grain non
 * enrichi n'aurait pas sa place dans la base — ce n'est pas une option ici.
 */
export function ImportConsole({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('teams');
  const [graphOk, setGraphOk] = useState<boolean | null>(null);
  const [teams, setTeams] = useState<{ id: string; displayName: string }[] | null>(null);
  const [channels, setChannels] = useState<{ id: string; displayName: string }[] | null>(null);

  // Champs de formulaire
  const [webUrl, setWebUrl] = useState('');
  const [depth, setDepth] = useState(1);
  const [maxPages, setMaxPages] = useState(50);
  const [spUrl, setSpUrl] = useState('');
  const [team, setTeam] = useState(''); // envoyé au serveur : id si choisi dans la liste, nom si saisi à la main
  const [teamId, setTeamId] = useState<string | null>(null); // id Graph du choix courant → charge ses canaux
  const [channel, setChannel] = useState(''); // nom du canal envoyé au serveur (vide = tous)
  const [withDocs, setWithDocs] = useState(false);

  const [job, setJob] = useState<ImportJob | null>(null);
  const [launching, setLaunching] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // État Graph + liste des équipes (SharePoint/Teams nécessitent M365 côté serveur).
  useEffect(() => {
    void api.import.graphStatus().then((s) => setGraphOk(s.connected)).catch(() => setGraphOk(false));
  }, []);
  useEffect(() => {
    if (tab === 'teams' && graphOk && teams === null) {
      void api.import.teams().then(setTeams).catch(() => setTeams([]));
    }
  }, [tab, graphOk, teams]);
  // Canaux de l'équipe choisie — évite de se tromper de nom (liste plutôt que saisie).
  useEffect(() => {
    if (graphOk && teamId) {
      setChannels(null);
      void api.import.channels(teamId).then(setChannels).catch(() => setChannels([]));
    }
  }, [graphOk, teamId]);

  // Polling tant que le job tourne.
  useEffect(() => {
    if (!job || job.status !== 'running') return;
    const h = setTimeout(() => {
      void api.import.job(job.id).then(setJob).catch(() => {});
    }, 1500);
    return () => clearTimeout(h);
  }, [job]);

  const buildSource = (): ImportSourceBody | null => {
    if (tab === 'web') return webUrl.trim() ? { type: 'web', url: webUrl.trim(), depth, max: maxPages } : null;
    if (tab === 'sharepoint') return spUrl.trim() ? { type: 'sharepoint', url: spUrl.trim() } : null;
    return team.trim() ? { type: 'teams', team: team.trim(), channel: channel.trim() || undefined, docs: withDocs } : null;
  };

  const launch = async (mode: 'estimate' | 'run'): Promise<void> => {
    const source = buildSource();
    if (!source) {
      setFormError(t('import.fillForm'));
      return;
    }
    setFormError(null);
    setLaunching(true);
    try {
      setJob(await api.import.createJob({ mode, source }));
    } catch {
      setFormError(t('error.generic'));
    }
    setLaunching(false);
  };

  const busy = launching || job?.status === 'running';
  const graphNeeded = tab !== 'web';

  const usd = (n: number) => `${n.toFixed(n < 1 ? 4 : 2)} $`;

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={t('import.title')} onClick={onClose}>
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-surface shadow-xl ring-1 ring-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">{t('import.title')}</h2>
            <p className="text-xs text-muted">{t('import.subtitle')}</p>
          </div>
          <button onClick={onClose} aria-label={t('import.close')} className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-fg">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4">
          {/* Choix de la source */}
          <div className="mb-4 flex gap-2">
            {TABS.map(({ key, icon: Icon }) => (
              <button
                key={key}
                onClick={() => { setTab(key); setJob(null); setFormError(null); }}
                className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ring-1 transition ${
                  tab === key ? 'bg-accent text-accent-fg ring-accent' : 'bg-surface-2 text-muted ring-transparent hover:text-fg'
                }`}
              >
                <Icon className="h-4 w-4" /> {t(`import.source.${key}`)}
              </button>
            ))}
          </div>

          {graphNeeded && graphOk === false && (
            <p className="mb-3 flex items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 ring-1 ring-amber-500/20 dark:text-amber-300">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t('import.graphOff')}
            </p>
          )}

          {/* Rappel du périmètre : l'indexation lit sous un compte de service dédié —
              il faut lui donner l'accès à l'équipe/au site AVANT d'importer. */}
          {graphNeeded && (
            <p className="mb-3 flex items-start gap-2 rounded-lg bg-accent/5 px-3 py-2 text-xs text-fg ring-1 ring-accent/20">
              <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
              <span>{t('import.serviceAccount', { account: SERVICE_ACCOUNT })}</span>
            </p>
          )}

          {/* Formulaire selon la source */}
          <div className="space-y-3">
            {tab === 'web' && (
              <>
                <Field label={t('import.web.url')}>
                  <input value={webUrl} onChange={(e) => setWebUrl(e.target.value)} placeholder="https://…" className={inputCls} />
                </Field>
                <div className="flex gap-3">
                  <Field label={t('import.web.depth')}>
                    <input type="number" min={0} max={3} value={depth} onChange={(e) => setDepth(Number(e.target.value))} className={inputCls} />
                  </Field>
                  <Field label={t('import.web.max')}>
                    <input type="number" min={1} max={200} value={maxPages} onChange={(e) => setMaxPages(Number(e.target.value))} className={inputCls} />
                  </Field>
                </div>
              </>
            )}

            {tab === 'sharepoint' && (
              <Field label={t('import.sp.url')}>
                <input value={spUrl} onChange={(e) => setSpUrl(e.target.value)} placeholder="https://…/sites/…" className={inputCls} />
              </Field>
            )}

            {tab === 'teams' && (
              <>
                <Field label={t('import.teams.pick')}>
                  {graphOk && teams ? (
                    <select
                      value={teamId ?? ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        setTeam(id);
                        setTeamId(id || null);
                        setChannel('');
                        setChannels(null);
                      }}
                      className={inputCls}
                    >
                      <option value="">{t('import.teams.placeholder')}</option>
                      {teams.map((tm) => (
                        <option key={tm.id} value={tm.id}>{tm.displayName}</option>
                      ))}
                    </select>
                  ) : (
                    <input value={team} onChange={(e) => setTeam(e.target.value)} placeholder={t('import.teams.manual')} className={inputCls} />
                  )}
                </Field>
                <Field label={t('import.teams.channel')}>
                  {graphOk && teamId ? (
                    channels === null ? (
                      <div className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-sm text-muted">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('import.teams.loadingChannels')}
                      </div>
                    ) : (
                      <select value={channel} onChange={(e) => setChannel(e.target.value)} className={inputCls}>
                        <option value="">{t('import.teams.allChannels')}</option>
                        {channels.map((c) => (
                          <option key={c.id} value={c.displayName}>{c.displayName}</option>
                        ))}
                      </select>
                    )
                  ) : (
                    <input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder={t('import.teams.channelPh')} className={inputCls} />
                  )}
                </Field>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" checked={withDocs} onChange={(e) => setWithDocs(e.target.checked)} className="h-4 w-4 accent-accent" />
                  {t('import.teams.docs')}
                </label>
              </>
            )}
          </div>

          {/* Enrichissement IA : systématique, pas une option — on l'affiche pour que ce soit visuel, sans case à décocher. */}
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-accent/5 px-3 py-2 text-sm ring-1 ring-accent/20">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
            <div>
              <p className="flex items-center gap-1.5 font-medium text-fg">
                {t('import.enrich')}
                <span className="rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                  {t('import.always')}
                </span>
              </p>
              <p className="text-xs text-muted">{t('import.enrichHint')}</p>
            </div>
          </div>

          {formError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{formError}</p>}

          {/* Actions */}
          <div className="mt-4 flex items-center gap-2">
            <button onClick={() => void launch('estimate')} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-2 text-sm font-medium text-fg transition hover:bg-surface disabled:opacity-50">
              <Calculator className="h-4 w-4" /> {t('import.estimate')}
            </button>
            <button onClick={() => void launch('run')} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {t('import.run')}
            </button>
          </div>

          {/* Suivi du job */}
          {job && (
            <div className="mt-4 rounded-xl bg-surface-2 px-4 py-3 text-sm">
              {job.scope && <p className="mb-2 font-medium">{job.scope}</p>}
              {job.status === 'running' && (
                <>
                  <p className="flex items-center gap-2 text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {job.mode === 'estimate' ? t('import.estimating') : t('import.running')}
                    {job.total > 0 && <span className="tabular-nums">· {job.done}/{job.total}</span>}
                  </p>
                  {job.total > 0 && (
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface">
                      <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${Math.round((job.done / job.total) * 100)}%` }} />
                    </div>
                  )}
                </>
              )}
              {job.status === 'done' && job.mode === 'estimate' && (
                <div className="space-y-1">
                  <p>{t('import.estResult', { sources: job.total, grains: job.grains })}</p>
                  {job.costUsd != null && <p className="text-muted">{t('import.costIfEnrich', { cost: usd(job.costUsd) })}</p>}
                </div>
              )}
              {job.status === 'done' && job.mode === 'run' && (
                <p className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                  <Check className="h-4 w-4" /> {t('import.doneSummary', { docs: job.docs, grains: job.grains, skipped: job.skipped })}
                </p>
              )}
              {job.status === 'error' && (
                <p className="flex items-start gap-2 text-red-600 dark:text-red-400">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" /> {t('import.failed', { error: job.error ?? '' })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full rounded-lg bg-surface-2 px-3 py-2 text-sm ring-1 ring-border outline-none placeholder:text-muted focus:ring-accent';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block flex-1">
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      {children}
    </label>
  );
}
